import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Circle, Calendar, Menu, X, LogOut, UserSearch, Wallet, Crown, MessageCircle, Calculator, Save, History, ClipboardList, Sun, Moon, Target, Instagram, Mail } from "lucide-react";
import { BACKEND_URL } from "../config";
import TeamChat from "../components/TeamChat";
import SolicitudesPanel from "../components/SolicitudesPanel";
import ModelosManagementPanel from "../components/ModelosManagementPanel";
import MetasPanel from "../components/MetasPanel";
import JGMProPanel from "../components/JGMProPanel";
import EmailAliasesPanel from "../components/EmailAliasesPanel";
import CustomCalculator from "../components/CustomCalculator";
import { useTheme } from "../contexts/ThemeContext";

const API = `${BACKEND_URL}/api`;

const DAYS_ES = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
const DAYS_LABELS = {
  "lunes": "Lunes",
  "martes": "Martes",
  "miercoles": "Miércoles",
  "jueves": "Jueves",
  "viernes": "Viernes",
  "sabado": "Sábado",
  "domingo": "Domingo"
};

function AssistantDashboard() {
  const { user, logout } = useContext(AuthContext);
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState("lunes");
  
  // Models states
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [openModelDialog, setOpenModelDialog] = useState(false);
  
  // Timer states
  const [isWorking, setIsWorking] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [currentSeconds, setCurrentSeconds] = useState(0);
  const [totalBreakSeconds, setTotalBreakSeconds] = useState(0);
  const [activeSessionId, setActiveSessionId] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState("agenda");

  // Contabilidad states
  const [reportesQuincenales, setReportesQuincenales] = useState([]);
  const [quincenaInicio, setQuincenaInicio] = useState(new Date().toISOString().slice(0, 10));
  const [quincenaFin, setQuincenaFin] = useState(new Date(new Date().setDate(new Date().getDate() + 15)).toISOString().slice(0, 10));
  const [resumenQuincenal, setResumenQuincenal] = useState(null);
  const [historialPeriodos, setHistorialPeriodos] = useState([]);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState(null);
  const [openHistorialDialog, setOpenHistorialDialog] = useState(false);

  // UI States
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Profile states
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [profileData, setProfileData] = useState({ name: "", phone: "", profile_picture: "" });

  // Team Chat states
  const [teamChatOpen, setTeamChatOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  
  // Payslips states
  const [payslips, setPayslips] = useState([]);
  
  // Spenders/CRM states
  const [crmUsers, setCrmUsers] = useState([]);
  const [crmStats, setCrmStats] = useState(null);
  const [crmSearchTerm, setCrmSearchTerm] = useState("");
  const [crmFilterGasto, setCrmFilterGasto] = useState("all");
  const [openCrmDialog, setOpenCrmDialog] = useState(false);
  const [editingCrmUser, setEditingCrmUser] = useState(null);
  const [newCrmUser, setNewCrmUser] = useState({
    username: "", modelo_id: "", origen: "", gustos: "", solicitudes: "", nivel_gasto: "medium", notas: ""
  });

  // Notification refs
  const lastMessageCountRef = React.useRef(0);
  const audioRef = React.useRef(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  // Initialize notification sound
  useEffect(() => {
    audioRef.current = new Audio(`${BACKEND_URL}/api/sounds/notification.mp3`);
    audioRef.current.volume = 0.4;
    audioRef.current.load();
    
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Fetch chat unread count with notification
  const fetchChatUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/chat/unread-count`, { withCredentials: true });
      const newTotal = response.data.total || 0;
      
      // Play sound if new messages and chat is closed
      if (newTotal > lastMessageCountRef.current && !teamChatOpen && notificationsEnabled) {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('💬 Nuevo mensaje', {
            body: `Tienes ${newTotal} mensaje(s) sin leer`,
            icon: '/favicon.ico',
            tag: 'team-chat'
          });
        }
      }
      
      lastMessageCountRef.current = newTotal;
      setChatUnreadCount(newTotal);
    } catch (error) {
      console.error("Error fetching chat unread count:", error);
    }
  };

  useEffect(() => {
    if (!user || user.role !== "asistente") {
      navigate("/");
      return;
    }
    fetchTasks();
    checkActiveSession();
    fetchChatUnreadCount();
    
    // Polling para mensajes no leídos
    const chatInterval = setInterval(fetchChatUnreadCount, 30000);
    return () => clearInterval(chatInterval);
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === "modelos") {
      fetchModels();
    }
  }, [activeTab]);

  const checkActiveSession = async () => {
    try {
      const response = await axios.get(`${API}/work-session/my-session`, { withCredentials: true });
      if (response.data.active) {
        setIsWorking(true);
        setIsOnBreak(response.data.is_on_break);
        setCurrentSeconds(response.data.elapsed_seconds);
        setTotalBreakSeconds(response.data.total_break_seconds);
        setActiveSessionId(response.data.session_id);
      }
    } catch (error) {
      console.error("Error checking active session:", error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/agenda/current-week`, {
        withCredentials: true
      });
      setTasks(response.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Error al cargar tareas");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    try {
      await axios.put(
        `${API}/agenda/tasks/${taskId}`,
        { completed: !currentStatus },
        { withCredentials: true }
      );
      
      // Update local state
      setTasks(tasks.map(t => 
        t.id === taskId ? { ...t, completed: !currentStatus } : t
      ));
      
      toast.success(!currentStatus ? "¡Tarea completada!" : "Tarea marcada como pendiente");
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Error al actualizar tarea");
    }
  };

  // Timer functions
  useEffect(() => {
    let interval;
    if (isWorking && !isOnBreak) {
      interval = setInterval(() => {
        setCurrentSeconds(prev => prev + 1);
      }, 1000);
    } else if (isWorking && isOnBreak) {
      interval = setInterval(() => {
        setTotalBreakSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isWorking, isOnBreak]);

  // Sync with backend every 30 seconds
  useEffect(() => {
    let syncInterval;
    if (isWorking) {
      syncInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/work-session/my-session`, { withCredentials: true });
          if (response.data.active) {
            setCurrentSeconds(response.data.elapsed_seconds);
            setTotalBreakSeconds(response.data.total_break_seconds);
          } else {
            // Sesión ya no está activa
            setIsWorking(false);
            setIsOnBreak(false);
          }
        } catch (error) {
          console.error("Error syncing session:", error);
        }
      }, 30000); // Sync every 30 seconds
    }
    return () => clearInterval(syncInterval);
  }, [isWorking]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartWork = async () => {
    try {
      await axios.post(`${API}/work-session/start`, {}, { withCredentials: true });
      setIsWorking(true);
      setCurrentSeconds(0);
      setTotalBreakSeconds(0);
      toast.success("¡Turno iniciado!");
    } catch (error) {
      console.error("Error starting work:", error);
      toast.error("Error al iniciar turno");
    }
  };

  const handleStartBreak = async () => {
    try {
      await axios.post(`${API}/work-session/break`, {}, { withCredentials: true });
      setIsOnBreak(true);
      toast.info("Break iniciado");
    } catch (error) {
      console.error("Error starting break:", error);
      toast.error("Error al iniciar break");
    }
  };

  const handleEndBreak = async () => {
    try {
      await axios.post(`${API}/work-session/resume`, {}, { withCredentials: true });
      setIsOnBreak(false);
      toast.success("Break finalizado, continúa trabajando");
    } catch (error) {
      console.error("Error ending break:", error);
      toast.error("Error al finalizar break");
    }
  };

  const handleEndWork = async () => {
    try {
      await axios.post(`${API}/work-session/end`, {}, { withCredentials: true });
      const totalHours = (currentSeconds / 3600).toFixed(2);
      const breakMinutes = (totalBreakSeconds / 60).toFixed(0);
      
      toast.success(`Turno finalizado: ${totalHours} horas trabajadas, ${breakMinutes} minutos de break`);
      
      setIsWorking(false);
      setIsOnBreak(false);
      setCurrentSeconds(0);
      setTotalBreakSeconds(0);
    } catch (error) {
      console.error("Error ending work:", error);
      toast.error("Error al finalizar turno");
    }
  };

  // Función para finalizar turno automáticamente (por inactividad)
  const handleAutoEndWork = async () => {
    if (!isWorking) return;
    
    try {
      await axios.post(`${API}/work-session/end`, {}, { withCredentials: true });
      const totalHours = (currentSeconds / 3600).toFixed(2);
      
      // Guardar reporte automáticamente
      const reportData = {
        date: new Date().toISOString().split('T')[0],
        hours_worked: parseFloat(totalHours),
        money_collected: 0,
        notes: "Turno finalizado automáticamente por inactividad"
      };
      
      await axios.post(`${API}/reports`, reportData, { withCredentials: true });
      
      setIsWorking(false);
      setIsOnBreak(false);
      setCurrentSeconds(0);
      setTotalBreakSeconds(0);
      
      console.log(`Turno guardado automáticamente: ${totalHours} horas`);
    } catch (error) {
      console.error("Error auto-ending work:", error);
    }
  };

  // Models management functions
  const fetchModels = async () => {
    try {
      const response = await axios.get(`${API}/models`, { withCredentials: true });
      setModels(response.data);
    } catch (error) {
      console.error("Error fetching models:", error);
      toast.error("Error al cargar modelos");
    }
  };

  const handleUpdateModel = async () => {
    if (!selectedModel) return;
    
    try {
      await axios.put(`${API}/models/${selectedModel.id}`, {
        name: selectedModel.name,
        biografia: selectedModel.biografia,
        usuarios_potenciales: selectedModel.usuarios_potenciales,
        metodologia_chat: selectedModel.metodologia_chat,
        expresion_chat: selectedModel.expresion_chat,
        status: selectedModel.status,
        porcentaje_agencia: selectedModel.porcentaje_agencia
      }, { withCredentials: true });
      
      toast.success("Modelo actualizado exitosamente");
      setOpenModelDialog(false);
      setSelectedModel(null);
      fetchModels();
    } catch (error) {
      toast.error("Error al actualizar modelo");
    }
  };

  const handleDeleteModel = async (modelId) => {
    try {
      await axios.delete(`${API}/models/${modelId}`, { withCredentials: true });
      toast.success("Modelo eliminado exitosamente");
      fetchModels();
    } catch (error) {
      toast.error("Error al eliminar modelo");
    }
  };

  // Contabilidad functions
  const fetchReportesQuincenales = async () => {
    try {
      const response = await axios.get(`${API}/contabilidad/quincenal/resumen`, {
        params: {
          quincena_inicio: quincenaInicio,
          quincena_fin: quincenaFin
        },
        withCredentials: true
      });
      setResumenQuincenal(response.data);
      setReportesQuincenales(response.data.reportes || []);
      
      // Si no hay reportes, inicializar con modelos activos
      if (!response.data.reportes || response.data.reportes.length === 0) {
        initializeReportesFromModels();
      }
    } catch (error) {
      console.error("Error fetching reportes:", error);
      // Si hay error, inicializar con modelos activos
      initializeReportesFromModels();
    }
  };

  const initializeReportesFromModels = async () => {
    try {
      const response = await axios.get(`${API}/models`, { withCredentials: true });
      const modelosActivos = response.data.filter(m => m.status === 'activa');
      const reportesIniciales = modelosActivos.map(modelo => ({
        modelo_id: modelo.id,
        modelo_name: modelo.name,
        año_ingreso: modelo.año_ingreso || new Date().getFullYear(),
        porcentaje_agencia: modelo.porcentaje_agencia || 40,
        valor_neto: 0,
        valor_neto_modelo: 0,
        valor_neto_agencia: 0
      }));
      setReportesQuincenales(reportesIniciales);
    } catch (error) {
      console.error("Error loading models:", error);
    }
  };

  const handleUpdateLocalReporte = (modeloId, field, value) => {
    setReportesQuincenales(prev => 
      prev.map(reporte => 
        reporte.modelo_id === modeloId 
          ? { ...reporte, [field]: parseFloat(value) || 0 }
          : reporte
      )
    );
  };

  const handleGuardarReporteQuincenal = async () => {
    try {
      const reportesConValor = reportesQuincenales.filter(r => r.valor_neto > 0);
      
      if (reportesConValor.length === 0) {
        toast.error("No hay datos para guardar");
        return;
      }

      for (const reporte of reportesConValor) {
        if (reporte.id) {
          await axios.put(`${API}/contabilidad/quincenal/${reporte.id}`, {
            valor_neto: reporte.valor_neto,
            porcentaje_agencia: reporte.porcentaje_agencia,
            valor_neto_modelo: reporte.valor_neto * ((100 - reporte.porcentaje_agencia) / 100),
            valor_neto_agencia: reporte.valor_neto * (reporte.porcentaje_agencia / 100)
          }, { withCredentials: true });
        } else {
          await axios.post(`${API}/contabilidad/quincenal`, {
            modelo_id: reporte.modelo_id,
            quincena_inicio: quincenaInicio,
            quincena_fin: quincenaFin,
            porcentaje_agencia: reporte.porcentaje_agencia,
            valor_neto: reporte.valor_neto,
            valor_neto_modelo: reporte.valor_neto * ((100 - reporte.porcentaje_agencia) / 100),
            valor_neto_agencia: reporte.valor_neto * (reporte.porcentaje_agencia / 100)
          }, { withCredentials: true });
        }
      }
      
      toast.success(`Reporte quincenal guardado exitosamente (${reportesConValor.length} modelos)`);
      fetchReportesQuincenales();
    } catch (error) {
      console.error("Error saving reporte:", error);
      toast.error("Error al guardar el reporte");
    }
  };

  const handleCerrarPeriodo = async () => {
    if (!confirm(`¿Cerrar el periodo ${quincenaInicio} a ${quincenaFin}? No podrás editarlo después.`)) {
      return;
    }
    
    try {
      await axios.post(`${API}/contabilidad/quincenal/cerrar-periodo`, null, {
        params: {
          quincena_inicio: quincenaInicio,
          quincena_fin: quincenaFin
        },
        withCredentials: true
      });
      
      toast.success("Periodo cerrado exitosamente");
      fetchReportesQuincenales();
      fetchHistorialPeriodos();
    } catch (error) {
      console.error("Error cerrando periodo:", error);
      toast.error("Error al cerrar el periodo");
    }
  };

  const fetchHistorialPeriodos = async () => {
    try {
      const response = await axios.get(`${API}/contabilidad/quincenal/historial`, {
        withCredentials: true
      });
      setHistorialPeriodos(response.data);
    } catch (error) {
      console.error("Error fetching historial:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "contabilidad") {
      fetchReportesQuincenales();
      fetchHistorialPeriodos();
    }
  }, [activeTab, quincenaInicio, quincenaFin]);

  // Fetch payslips
  const fetchPayslips = async () => {
    try {
      const response = await axios.get(`${API}/payslips`, { withCredentials: true });
      setPayslips(response.data);
    } catch (error) {
      console.error("Error fetching payslips:", error);
    }
  };

  // CRM Functions
  const fetchCrmUsers = async () => {
    try {
      let url = `${API}/crm/users?`;
      if (crmSearchTerm) url += `search=${encodeURIComponent(crmSearchTerm)}&`;
      if (crmFilterGasto !== "all") url += `nivel_gasto=${crmFilterGasto}&`;
      const response = await axios.get(url, { withCredentials: true });
      setCrmUsers(response.data);
    } catch (error) {
      console.error("Error fetching CRM users:", error);
    }
  };

  const fetchCrmStats = async () => {
    try {
      const response = await axios.get(`${API}/crm/stats`, { withCredentials: true });
      setCrmStats(response.data);
    } catch (error) {
      console.error("Error fetching CRM stats:", error);
    }
  };

  const handleCreateCrmUser = async () => {
    if (!newCrmUser.username || !newCrmUser.modelo_id) {
      toast.error("Usuario y modelo son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/crm/users`, newCrmUser, { withCredentials: true });
      toast.success("Usuario creado");
      setOpenCrmDialog(false);
      setNewCrmUser({ username: "", modelo_id: "", origen: "", gustos: "", solicitudes: "", nivel_gasto: "medium", notas: "" });
      fetchCrmUsers();
      fetchCrmStats();
    } catch (error) {
      toast.error("Error al crear usuario");
    }
  };

  const handleUpdateCrmUser = async () => {
    if (!editingCrmUser) return;
    try {
      await axios.put(`${API}/crm/users/${editingCrmUser.id}`, editingCrmUser, { withCredentials: true });
      toast.success("Usuario actualizado");
      setEditingCrmUser(null);
      fetchCrmUsers();
    } catch (error) {
      toast.error("Error al actualizar usuario");
    }
  };

  const handleDeleteCrmUser = async (userId) => {
    if (!window.confirm("Eliminar este usuario?")) return;
    try {
      await axios.delete(`${API}/crm/users/${userId}`, { withCredentials: true });
      toast.success("Usuario eliminado");
      fetchCrmUsers();
      fetchCrmStats();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Profile Functions
  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${API}/users/profile/me`, {
        name: profileData.name,
        phone: profileData.phone
      }, { withCredentials: true });
      toast.success("Perfil actualizado");
      setOpenProfileDialog(false);
    } catch (error) {
      toast.error("Error al actualizar perfil");
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPicture(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        await axios.post(`${API}/users/profile/picture`, { picture: base64 }, { withCredentials: true });
        setProfileData(prev => ({ ...prev, profile_picture: base64 }));
        toast.success("Foto actualizada");
        setUploadingPicture(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Error al subir foto");
      setUploadingPicture(false);
    }
  };

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === "spenders") {
      fetchCrmUsers();
      fetchCrmStats();
    } else if (activeTab === "colillas") {
      fetchPayslips();
    }
  }, [activeTab, crmSearchTerm, crmFilterGasto]);

  // Load profile data
  useEffect(() => {
    if (user) {
      setProfileData({ name: user.name || "", phone: user.phone || "", profile_picture: user.picture || "" });
    }
  }, [user]);


  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-xl font-semibold text-white">Cargando...</div>
      </div>
    );
  }

  // Group tasks by day
  const tasksByDay = DAYS_ES.reduce((acc, day) => {
    acc[day] = tasks.filter(t => t.day === day).sort((a, b) => a.order - b.order);
    return acc;
  }, {});

  const selectedDayTasks = tasksByDay[selectedDay] || [];

  // Handler para cuando el monitoreo detecte inactividad
  const handleInactividad = () => {
    toast.error("Has sido desconectado por inactividad");
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Floating Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setSidebarOpen(false)} />
      )}
      
      {/* Floating Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-black/95 backdrop-blur-xl border-r border-white/10 transition-all duration-300 z-50 flex flex-col shadow-[4px_0_24px_rgba(0,0,0,0.5)] ${sidebarOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'} w-64`}>
        <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10 transition-colors">
          <X size={20} className="text-white/50" />
        </button>
        
        <div className="h-20 flex items-center px-4 border-b border-white/5">
          <img src="https://customer-assets.emergentagent.com/job_46a23635-e3a8-4c01-958f-4141d61cb2ea/artifacts/81fohwa6_Disen%CC%83o%20sin%20ti%CC%81tulo.png" alt="JG Models" className="h-14 w-auto object-contain" />
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="px-3 space-y-1">
            {[
              { id: 'agenda', label: 'Agenda', icon: Calendar },
              { id: 'modelos', label: 'Modelos', icon: Crown },
              { id: 'calculadora', label: 'Calculadora', icon: Calculator },
              { id: 'spenders', label: 'Spenders', icon: UserSearch },
              { id: 'metas', label: 'Metas', icon: Target },
              { id: 'solicitudes', label: 'Solicitudes', icon: ClipboardList },
              { id: 'jgmpro', label: 'JGM PRO', icon: Instagram },
              { id: 'email-aliases', label: 'Buzón IG', icon: Mail },
              { id: 'contabilidad', label: 'Contabilidad', icon: Calculator },
              { id: 'colillas', label: 'Mis Colillas', icon: Wallet },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeTab === item.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                <item.icon size={20} />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>

        <div className="p-3 border-t border-white/5 space-y-1">
          <button onClick={toggleTheme} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-all">
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
            <span className="text-sm font-medium">{isDark ? 'Modo Claro' : 'Modo Oscuro'}</span>
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-all">
            <LogOut size={20} />
            <span className="text-sm font-medium">Cerrar Sesion</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full">
        {/* Top Bar */}
        <div className="h-16 bg-black/80 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
              <Menu size={20} className="text-white/70" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-white">
                {activeTab === 'agenda' && 'Agenda'}
                {activeTab === 'modelos' && 'Modelos'}
                {activeTab === 'calculadora' && 'Calculadora'}
                {activeTab === 'spenders' && 'Spenders'}
                {activeTab === 'metas' && 'Metas'}
                {activeTab === 'solicitudes' && 'Solicitudes'}
                {activeTab === 'jgmpro' && 'JGM PRO'}
                {activeTab === 'email-aliases' && 'Buzón IG'}
                {activeTab === 'contabilidad' && 'Contabilidad'}
                {activeTab === 'colillas' && 'Mis Colillas'}
              </h1>
              <p className="text-sm text-gray-500">Bienvenido, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${isWorking ? (isOnBreak ? 'bg-orange-600/20 text-orange-400' : 'bg-green-600/20 text-green-400') : 'bg-zinc-800 text-gray-400'}`}>
              {isWorking ? (isOnBreak ? "En Break" : "Trabajando") : "Sin turno"}
            </span>
            {/* Chat Button */}
            <button
              onClick={() => setTeamChatOpen(true)}
              className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-[#0a0a0a] border border-white/[0.08] hover:bg-zinc-800 transition-colors"
              title="Chat del Equipo"
            >
              <MessageCircle className="w-4 h-4 text-blue-400" />
              {chatUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center text-[10px]">
                  {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                </span>
              )}
            </button>
            <button onClick={() => { setProfileData({ name: user?.name || "", phone: user?.phone || "", profile_picture: user?.picture || "" }); setOpenProfileDialog(true); }} className="flex items-center gap-2 hover:bg-white/5 rounded-lg px-2 py-1 transition-colors cursor-pointer">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{user?.name?.charAt(0) || 'A'}</span>
                </div>
              )}
              <span className="text-white text-sm hidden md:block">{user?.name}</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Time Tracker Card - shown on agenda tab */}
          {activeTab === 'agenda' && (
            <>
              <Card className="mb-6 bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                <CardContent className="pt-4 pb-4">
                  <div className="text-center space-y-4">
                    <div>
                      <h2 className="text-xl font-bold text-white mb-1">Control de Turno</h2>
                      <p className="text-gray-400 text-xs">{isWorking ? (isOnBreak ? "En Break" : "Trabajando") : "Listo para comenzar"}</p>
                    </div>
                    <div className="relative inline-block">
                      <div className="text-4xl font-bold text-white font-mono tracking-wider">{formatTime(currentSeconds)}</div>
                      <div className="text-xs text-gray-400 mt-1">{isWorking ? `Break: ${formatTime(totalBreakSeconds)}` : "HH:MM:SS"}</div>
                    </div>
                    <div className="flex gap-3 justify-center flex-wrap">
                      {!isWorking ? (
                        <Button onClick={handleStartWork} className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 text-base rounded-full">Iniciar Turno</Button>
                      ) : (
                        <>
                          {!isOnBreak ? (
                            <Button onClick={handleStartBreak} className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 text-base rounded-full">Tomar Break</Button>
                          ) : (
                            <Button onClick={handleEndBreak} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-base rounded-full">Reanudar</Button>
                          )}
                          <Button onClick={handleEndWork} className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 text-base rounded-full">Finalizar</Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Day Selector */}
              <Card className="mb-6 bg-[#0a0a0a] border border-white/[0.08] rounded-2xl">
                <CardContent className="pt-4">
                  <div className="flex flex-wrap gap-2 justify-center">
                    {DAYS_ES.map((day) => (
                      <Button
                        key={day}
                        onClick={() => setSelectedDay(day)}
                        className={`${selectedDay === day ? 'bg-purple-600 hover:bg-purple-700' : 'bg-zinc-800 hover:bg-zinc-700 text-gray-300'}`}
                      >
                        {DAYS_LABELS[day]}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Tasks for Selected Day */}
              <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-white">Tareas - {DAYS_LABELS[selectedDay]}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedDayTasks.length === 0 ? (
                      <p className="text-center text-gray-500 py-4">No hay tareas para este dia</p>
                    ) : (
                      selectedDayTasks.map((task) => (
                        <div key={task.id} className={`flex items-center gap-3 p-3 rounded-lg ${task.completed ? 'bg-green-900/20' : 'bg-zinc-800'}`}>
                          <button onClick={() => toggleTaskComplete(task.id, !task.completed)}>
                            {task.completed ? <CheckCircle2 className="text-green-500" size={24} /> : <Circle className="text-gray-500" size={24} />}
                          </button>
                          <span className={`flex-1 ${task.completed ? 'text-gray-500 line-through' : 'text-white'}`}>{task.title}</span>
                          <span className={`text-xs px-2 py-1 rounded ${task.priority === 'importante' ? 'bg-red-500/20 text-red-400' : task.priority === 'especial' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>
                            {task.priority}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Modelos Tab */}
          {activeTab === 'modelos' && (
            <ModelosManagementPanel />
          )}

          {/* Spenders Tab */}
          {activeTab === 'spenders' && (
            <div className="space-y-6">
              {crmStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl p-4">
                    <p className="text-gray-400 text-sm">Total Usuarios</p>
                    <p className="text-3xl font-bold text-white">{crmStats.total}</p>
                  </Card>
                  <Card className="bg-[#0a0a0a] border border-green-500/20 rounded-2xl p-4">
                    <p className="text-green-400 text-sm">High Spenders</p>
                    <p className="text-3xl font-bold text-green-400">{crmStats.by_spending_level?.high || 0}</p>
                  </Card>
                  <Card className="bg-[#0a0a0a] border border-yellow-500/20 rounded-2xl p-4">
                    <p className="text-yellow-400 text-sm">Medium</p>
                    <p className="text-3xl font-bold text-yellow-400">{crmStats.by_spending_level?.medium || 0}</p>
                  </Card>
                  <Card className="bg-[#0a0a0a] border border-gray-500/20 rounded-2xl p-4">
                    <p className="text-gray-400 text-sm">Low</p>
                    <p className="text-3xl font-bold text-gray-300">{crmStats.by_spending_level?.low || 0}</p>
                  </Card>
                </div>
              )}

              <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl">
                <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <CardTitle className="text-white">Base de Datos</CardTitle>
                  <Dialog open={openCrmDialog} onOpenChange={setOpenCrmDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-blue-600 hover:bg-blue-700">+ Nuevo Usuario</Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
                      <DialogHeader><DialogTitle>Agregar Usuario</DialogTitle></DialogHeader>
                      <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
                        <div>
                          <Label className="text-gray-300">Username *</Label>
                          <Input value={newCrmUser.username} onChange={(e) => setNewCrmUser({ ...newCrmUser, username: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="@usuario" />
                        </div>
                        <div>
                          <Label className="text-gray-300">Modelo *</Label>
                          <Select value={newCrmUser.modelo_id} onValueChange={(v) => setNewCrmUser({ ...newCrmUser, modelo_id: v })}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {models.map((m) => (<SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-gray-300">Nivel de Gasto</Label>
                          <Select value={newCrmUser.nivel_gasto} onValueChange={(v) => setNewCrmUser({ ...newCrmUser, nivel_gasto: v })}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="low" className="text-gray-300">Low</SelectItem>
                              <SelectItem value="medium" className="text-yellow-400">Medium</SelectItem>
                              <SelectItem value="high" className="text-green-400">High</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-gray-300">Origen</Label>
                          <Input value={newCrmUser.origen} onChange={(e) => setNewCrmUser({ ...newCrmUser, origen: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="Pais" />
                        </div>
                        <div>
                          <Label className="text-gray-300">Gustos</Label>
                          <Input value={newCrmUser.gustos} onChange={(e) => setNewCrmUser({ ...newCrmUser, gustos: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
                        </div>
                        <div>
                          <Label className="text-gray-300">Notas</Label>
                          <Input value={newCrmUser.notas} onChange={(e) => setNewCrmUser({ ...newCrmUser, notas: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
                        </div>
                        <Button onClick={handleCreateCrmUser} className="w-full bg-blue-600 hover:bg-blue-700">Guardar</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <Input placeholder="Buscar username..." value={crmSearchTerm} onChange={(e) => setCrmSearchTerm(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white flex-1" />
                    <Select value={crmFilterGasto} onValueChange={setCrmFilterGasto}>
                      <SelectTrigger className="w-full md:w-40 bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="all" className="text-white">Todos</SelectItem>
                        <SelectItem value="high" className="text-green-400">High</SelectItem>
                        <SelectItem value="medium" className="text-yellow-400">Medium</SelectItem>
                        <SelectItem value="low" className="text-gray-300">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-2 text-gray-400 font-medium">Username</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium">Modelo</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium">Nivel</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crmUsers.length === 0 ? (
                          <tr><td colSpan="4" className="text-center py-8 text-gray-500">No hay usuarios</td></tr>
                        ) : (
                          crmUsers.map((u) => (
                            <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-3 px-2 text-white">{u.username}</td>
                              <td className="py-3 px-2 text-gray-300">{u.modelo_name}</td>
                              <td className="py-3 px-2">
                                <span className={`px-2 py-1 rounded-full text-xs ${u.nivel_gasto === 'high' ? 'bg-green-500/20 text-green-400' : u.nivel_gasto === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                  {u.nivel_gasto}
                                </span>
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" className="border-blue-500/50 text-blue-400" onClick={() => setEditingCrmUser(u)}>Ver</Button>
                                  <Button size="sm" variant="outline" className="border-red-500/50 text-red-400" onClick={() => handleDeleteCrmUser(u.id)}>X</Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Dialog open={!!editingCrmUser} onOpenChange={() => setEditingCrmUser(null)}>
                <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
                  <DialogHeader><DialogTitle>Editar: {editingCrmUser?.username}</DialogTitle></DialogHeader>
                  {editingCrmUser && (
                    <div className="space-y-4 py-4">
                      <div><Label className="text-gray-300">Username</Label><Input value={editingCrmUser.username} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, username: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                      <div><Label className="text-gray-300">Nivel de Gasto</Label>
                        <Select value={editingCrmUser.nivel_gasto} onValueChange={(v) => setEditingCrmUser({ ...editingCrmUser, nivel_gasto: v })}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="low" className="text-gray-300">Low</SelectItem>
                            <SelectItem value="medium" className="text-yellow-400">Medium</SelectItem>
                            <SelectItem value="high" className="text-green-400">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-gray-300">Origen</Label><Input value={editingCrmUser.origen || ''} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, origen: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                      <div><Label className="text-gray-300">Gustos</Label><Input value={editingCrmUser.gustos || ''} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, gustos: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                      <div><Label className="text-gray-300">Notas</Label><Input value={editingCrmUser.notas || ''} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, notas: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                      <Button onClick={handleUpdateCrmUser} className="w-full bg-blue-600 hover:bg-blue-700">Guardar</Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Contabilidad Tab */}
          {activeTab === 'contabilidad' && (
            <div className="space-y-6">
              {/* Period Selector */}
              <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                <CardHeader className="pb-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-purple-400" />
                      Reporte Quincenal
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Label className="text-gray-400 text-sm">Desde:</Label>
                        <Input
                          type="date"
                          value={quincenaInicio}
                          onChange={(e) => setQuincenaInicio(e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-white w-36"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-gray-400 text-sm">Hasta:</Label>
                        <Input
                          type="date"
                          value={quincenaFin}
                          onChange={(e) => setQuincenaFin(e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-white w-36"
                        />
                      </div>
                      <Button
                        onClick={() => setOpenHistorialDialog(true)}
                        variant="outline"
                        className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
                      >
                        <History className="w-4 h-4 mr-2" />
                        Historial
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Summary Cards */}
                  {resumenQuincenal && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-zinc-800/50 rounded-xl p-4 border border-white/5">
                        <p className="text-gray-400 text-sm mb-1">Total Neto</p>
                        <p className="text-2xl font-bold text-white">${resumenQuincenal.total_neto?.toLocaleString() || 0}</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-xl p-4 border border-white/5">
                        <p className="text-gray-400 text-sm mb-1">Total Modelos</p>
                        <p className="text-2xl font-bold text-green-400">${resumenQuincenal.total_modelos?.toLocaleString() || 0}</p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-xl p-4 border border-white/5">
                        <p className="text-gray-400 text-sm mb-1">Total Agencia</p>
                        <p className="text-2xl font-bold text-purple-400">${resumenQuincenal.total_agencia?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                  )}

                  {/* Models Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-gray-400 text-sm font-medium py-3 px-2">Modelo</th>
                          <th className="text-center text-gray-400 text-sm font-medium py-3 px-2">% Agencia</th>
                          <th className="text-center text-gray-400 text-sm font-medium py-3 px-2">Valor Neto</th>
                          <th className="text-center text-gray-400 text-sm font-medium py-3 px-2">Modelo</th>
                          <th className="text-center text-gray-400 text-sm font-medium py-3 px-2">Agencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportesQuincenales.map((reporte) => {
                          const valorModelo = reporte.valor_neto * ((100 - reporte.porcentaje_agencia) / 100);
                          const valorAgencia = reporte.valor_neto * (reporte.porcentaje_agencia / 100);
                          return (
                            <tr key={reporte.modelo_id} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                                    <span className="text-white text-xs font-bold">{reporte.modelo_name?.charAt(0) || 'M'}</span>
                                  </div>
                                  <span className="text-white font-medium">{reporte.modelo_name}</span>
                                </div>
                              </td>
                              <td className="py-3 px-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={reporte.porcentaje_agencia}
                                  onChange={(e) => handleUpdateLocalReporte(reporte.modelo_id, 'porcentaje_agencia', e.target.value)}
                                  className="w-20 bg-zinc-800 border-zinc-700 text-white text-center mx-auto"
                                />
                              </td>
                              <td className="py-3 px-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  value={reporte.valor_neto}
                                  onChange={(e) => handleUpdateLocalReporte(reporte.modelo_id, 'valor_neto', e.target.value)}
                                  className="w-28 bg-zinc-800 border-zinc-700 text-white text-center mx-auto"
                                  placeholder="$0"
                                />
                              </td>
                              <td className="py-3 px-2 text-center text-green-400 font-medium">
                                ${valorModelo.toLocaleString(undefined, {maximumFractionDigits: 0})}
                              </td>
                              <td className="py-3 px-2 text-center text-purple-400 font-medium">
                                ${valorAgencia.toLocaleString(undefined, {maximumFractionDigits: 0})}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-white/10">
                    <Button
                      onClick={handleGuardarReporteQuincenal}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Guardar Reporte
                    </Button>
                    <Button
                      onClick={handleCerrarPeriodo}
                      variant="outline"
                      className="bg-transparent border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                    >
                      Cerrar Periodo
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Historial Dialog */}
              <Dialog open={openHistorialDialog} onOpenChange={setOpenHistorialDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <History className="w-5 h-5 text-purple-400" />
                      Historial de Periodos
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-4">
                    {historialPeriodos.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No hay periodos cerrados</p>
                    ) : (
                      historialPeriodos.map((periodo, idx) => (
                        <Card key={idx} className="bg-zinc-800 border-zinc-700">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-center mb-3">
                              <div>
                                <p className="text-white font-medium">{periodo.quincena_inicio} - {periodo.quincena_fin}</p>
                                <p className="text-gray-400 text-sm">{periodo.reportes?.length || 0} modelos</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-white">${periodo.total_neto?.toLocaleString() || 0}</p>
                                <p className="text-green-400 text-sm">Modelos: ${periodo.total_modelos?.toLocaleString() || 0}</p>
                              </div>
                            </div>
                            <Button
                              onClick={() => {
                                setPeriodoSeleccionado(periodo);
                                setOpenHistorialDialog(false);
                              }}
                              variant="outline"
                              size="sm"
                              className="w-full bg-zinc-700 border-zinc-600 text-white hover:bg-zinc-600"
                            >
                              Ver Detalles
                            </Button>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Solicitudes Tab */}
          {activeTab === 'solicitudes' && (
            <SolicitudesPanel currentUserRole={user?.role} />
          )}

          {/* Calculadora Tab */}
          {activeTab === 'calculadora' && (
            <CustomCalculator />
          )}

          {/* Metas Tab */}
          {activeTab === 'metas' && (
            <MetasPanel />
          )}

          {/* JGM PRO Tab */}
          {activeTab === 'jgmpro' && (
            <JGMProPanel />
          )}

          {/* Buzón IG Tab */}
          {activeTab === 'email-aliases' && (
            <EmailAliasesPanel />
          )}

          {/* Colillas Tab */}
          {activeTab === 'colillas' && (
            <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl">
              <CardHeader>
                <CardTitle className="text-white">Mis Colillas de Pago</CardTitle>
              </CardHeader>
              <CardContent>
                {payslips.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No tienes colillas de pago aun</p>
                ) : (
                  <div className="space-y-4">
                    {payslips.map((payslip) => (
                      <Card key={payslip.id} className={`${payslip.pagado ? 'bg-green-500/5 border-green-500/40' : 'bg-zinc-800 border-zinc-700'} border`}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-4 gap-3 flex-wrap">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-lg font-bold text-white">{payslip.periodo}</h3>
                                {payslip.pagado ? (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30 font-semibold">
                                    ✓ Pagada
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 font-semibold">
                                    ⏳ Pendiente de pago
                                  </span>
                                )}
                              </div>
                              {payslip.pagado && payslip.fecha_pago && (
                                <p className="text-xs text-green-400 mt-1 font-medium">
                                  ✓ Pagada el {new Date(payslip.fecha_pago).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </p>
                              )}
                            </div>
                            <span className="text-green-400 font-bold text-xl">${payslip.total_pagado?.toLocaleString()} COP</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div><span className="text-gray-400">Salario Base:</span> <span className="text-white">${payslip.salario_base?.toLocaleString()}</span></div>
                            <div><span className="text-gray-400">Dias Trabajados:</span> <span className="text-white">{payslip.dias_trabajados}</span></div>
                            <div><span className="text-gray-400">Horas:</span> <span className="text-white">{payslip.horas_trabajadas?.toFixed(1)}</span></div>
                            <div><span className="text-gray-400">Bonificaciones:</span> <span className="text-green-400">${payslip.bonificaciones?.toLocaleString()}</span></div>
                          </div>
                          {payslip.observaciones && (
                            <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded text-sm">
                              <p className="text-amber-400 font-semibold">📝 Observación del administrador:</p>
                              <p className="text-amber-200 mt-1 whitespace-pre-wrap">{payslip.observaciones}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Profile Dialog */}
      <Dialog open={openProfileDialog} onOpenChange={setOpenProfileDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
          <DialogHeader><DialogTitle>Mi Perfil</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex flex-col items-center gap-4">
              {profileData.profile_picture ? (
                <img src={profileData.profile_picture} alt="" className="w-24 h-24 rounded-full object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <span className="text-white text-3xl font-bold">{user?.name?.charAt(0)}</span>
                </div>
              )}
              <label className="cursor-pointer">
                <span className="text-blue-400 text-sm hover:underline">{uploadingPicture ? 'Subiendo...' : 'Cambiar foto'}</span>
                <input type="file" accept="image/*" onChange={handleProfilePictureUpload} className="hidden" disabled={uploadingPicture} />
              </label>
            </div>
            <div><Label className="text-gray-300">Nombre</Label><Input value={profileData.name} onChange={(e) => setProfileData({ ...profileData, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
            <div><Label className="text-gray-300">Telefono</Label><Input value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
            <Button onClick={handleUpdateProfile} className="w-full bg-blue-600 hover:bg-blue-700">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Model Detail Dialog */}
      <Dialog open={openModelDialog} onOpenChange={setOpenModelDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedModel?.name}</DialogTitle>
          </DialogHeader>
          {selectedModel && (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Biografia</Label>
                <textarea value={selectedModel.biografia || ""} onChange={(e) => setSelectedModel({ ...selectedModel, biografia: e.target.value })} className="w-full min-h-[100px] p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white" placeholder="Personalidad, edad, gustos..." />
              </div>
              <div>
                <Label className="text-gray-300">Usuarios Potenciales</Label>
                <textarea value={selectedModel.usuarios_potenciales || ""} onChange={(e) => setSelectedModel({ ...selectedModel, usuarios_potenciales: e.target.value })} className="w-full min-h-[100px] p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white" placeholder="Tipo de usuarios..." />
              </div>
              <div>
                <Label className="text-gray-300">Metodologia de Chat</Label>
                <textarea value={selectedModel.metodologia_chat || ""} onChange={(e) => setSelectedModel({ ...selectedModel, metodologia_chat: e.target.value })} className="w-full min-h-[100px] p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white" placeholder="Estrategias..." />
              </div>
              <div>
                <Label className="text-gray-300">Expresion del Modelo</Label>
                <textarea value={selectedModel.expresion_chat || ""} onChange={(e) => setSelectedModel({ ...selectedModel, expresion_chat: e.target.value })} className="w-full min-h-[100px] p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white" placeholder="Tono, emojis..." />
              </div>
              <Button onClick={handleUpdateModel} className="w-full bg-purple-600 hover:bg-purple-700">Guardar Cambios</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Chat Modal */}
      <TeamChat 
        isOpen={teamChatOpen} 
        onClose={() => {
          setTeamChatOpen(false);
          fetchChatUnreadCount();
        }}
        currentUser={user}
      />
    </div>
  );
}

export default AssistantDashboard;
